import { useDraggable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import { formatDate, formatDistanceToNow } from "date-fns";
import { MouseEvent } from "react";

import { getObjectType } from "@/utils/libraryObjectUtils";
import { normalizeThumbnails } from "@/utils/thumbnails";

import { useTheme } from "../../components/ui-v2";
import { useDrive } from "./DriveContext";
import { DriveGridItem } from "./DriveGridItem";
import DriveItem, { EllipsisButton } from "./DriveItem";
import { DriveFileFragment } from "./queries.graphql";
import { DriveViewType } from "./types";
import { tableViewColumns } from "./utils";

type DriveFileViewProps = {
  viewType: DriveViewType;
  file: DriveFileFragment;
  isSelected: boolean;
  isSelectable: boolean;
  onClick: (event: MouseEvent) => void;
  onDoubleClick: (event: MouseEvent) => void;
  onContextMenu: (event: MouseEvent) => void;
};

export default function DriveFileView({
  viewType,
  file,
  isSelected,
  isSelectable,
  onClick,
  onDoubleClick,
  onContextMenu,
}: DriveFileViewProps) {
  const theme = useTheme();
  const { actionsDisabled } = useDrive();

  const {
    attributes,
    listeners,
    setNodeRef: draggableNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `file__${file.id}`,
    data: {
      supports: ["file"],
      item: file,
    },
  });

  const draggableStyle = transform
    ? {
        ...(isDragging && {
          pointerEvents: "none" as const, // this is necessary to prevent the link from being clicked while dragging
        }),
      }
    : undefined;

  return (
    <DriveItem
      title={`${file.name || "Untitled"} (created at ${formatDate(file.createdAt, "PPpp")})`}
      type="File"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      draggableStyle={draggableStyle}
      isSelected={isSelected}
      isDragging={isDragging}
      isSelectable={isSelectable}
      viewType={viewType}
      ref={draggableNodeRef}
      {...listeners}
      {...attributes}
    >
      {viewType === "table" && (
        <>
          {tableViewColumns.map((column, index: number) => {
            return (
              <Box
                key={column.id}
                sx={{
                  width: column.width,
                  flexGrow: column.width ? 0 : 1,
                  flexShrink: column.width ? 0 : 1,
                  overflow: "hidden",
                  display: "-webkit-box",
                  "-webkit-box-orient": "vertical",
                  "-webkit-line-clamp": "1",
                  paddingRight: 4,
                  paddingLeft: 4,

                  ...(index > 1 && {
                    fontWeight: 300,
                    color: theme.palette.secondary[2],
                  }),
                }}
              >
                {column.fileValue?.(file) || ""}
              </Box>
            );
          })}
          {!actionsDisabled && (
            <EllipsisButton onClick={onContextMenu} isSelected={isSelected} />
          )}
        </>
      )}
      {viewType === "grid" && (
        <DriveGridItem
          id={file.id}
          text={file.name ?? "Untitled"}
          subText={formatDistanceToNow(file.createdAt, {
            addSuffix: true,
          })}
          isSelected={isSelected}
          isDragging={isDragging}
          onContextMenu={onContextMenu}
          src={file.url ?? undefined}
          previewUrl={file.previewUrl ?? undefined}
          width={file.metadata?.width ?? undefined}
          thumbnails={
            file.thumbnails ? normalizeThumbnails(file.thumbnails) : undefined
          }
          fileType={getObjectType(file)}
          onFileClick={onClick}
          onFileDoubleClick={onDoubleClick}
        />
      )}
    </DriveItem>
  );
}
